import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Typography,
} from "@mui/material";
import React, { useContext } from "react";
import * as Yup from "yup";
import { Formik, FormikHelpers } from "formik";
import { ScreenFlowFormButtons } from "./ScreenFlowFormButtons";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { AppContext } from "../../../App";
import { HelpContent } from "../../shared/help";
import { objectVals2String } from "../../../utils/objectVals2String";

interface Values {
  countryId: string;
  languageCode: string;
  currentUserCountryId: number;
}

export type LanguageOption = {
  name: string;
  code: string;
};

export type RadioOption = {
  value: string;
  label?: string;
  help?: HelpContent;
  languageOptions?: LanguageOption[];
};

interface Props {
  errorText?: string;
  options: RadioOption[];
  onComplete: (countryId: number, languageCode: string) => Promise<boolean>;
  onBack?: () => void;
  currentUserCountryId: number;
}

export const ChangeCountryForm = ({
  errorText,
  options,
  onComplete,
  onBack,
  currentUserCountryId,
}: Props): JSX.Element => {
  const { setHelpContent } = useContext(AppContext);

  // https://formik.org/docs/api/formik
  const formikInit = {
    values: {
      countryId: "",
      languageCode: "",
      currentUserCountryId: currentUserCountryId,
    },
    schema: Yup.object().shape({
      countryId: Yup.string().required(errorText),
      languageCode: Yup.string().when(["countryId", "currentUserCountryId"], {
        is: (countryId: string, currentUserCountryId: number) =>
          parseInt(countryId, 10) != currentUserCountryId,
        then: Yup.string().required("Language is Required"),
        otherwise: Yup.string(),
      }),
    }),
  };

  const handleSubmit = async (
    values: Values,
    { setSubmitting, setStatus }: FormikHelpers<Values>
  ) => {
    setStatus("");
    try {
      const retCountryId = parseInt(values.countryId, 10);
      let retLanguageCode = values.languageCode;

      // Currently, English is only supported for the US.
      if (retCountryId == 1 && retLanguageCode != "en") {
        retLanguageCode = "en";
      }

      await onComplete(retCountryId, retLanguageCode);
    } catch (error) {
      setStatus("Sorry, error: " + ((error as Error)?.message || ""));
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={formikInit.values}
      validationSchema={formikInit.schema}
      onSubmit={handleSubmit}
      key={objectVals2String(formikInit.values)}
    >
      {({
        errors,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        status,
      }) => (
        <form onSubmit={handleSubmit}>
          <Grid
            container
            spacing={1}
            sx={{
              my: 1,
            }}
          >
            <Grid item xs={12} sm={8} sx={{ margin: "0 auto" }}>
              <RadioGroup
                aria-label="Country ID"
                name="countryId"
                value={values.countryId}
                onChange={handleChange}
              >
                {options.map((option: RadioOption) => {
                  return (
                    <React.Fragment key={`option-${option.value}`}>
                      <Box>
                        <FormControlLabel
                          value={option.value}
                          control={<Radio />}
                          label={option.label || option.value}
                          sx={{ mr: 0, my: "4px" }}
                        />
                        {option.help && (
                          <IconButton
                            onClick={() => {
                              if (option.help) {
                                if (option.help.href) {
                                  window.open(option.help.href);
                                } else {
                                  setHelpContent(option.help);
                                }
                              }
                            }}
                            aria-label="help"
                            color="secondary"
                          >
                            <HelpOutlineOutlinedIcon />
                          </IconButton>
                        )}
                      </Box>
                      <Box sx={{ ml: 3 }}>
                        {values.countryId == option.value &&
                          option.languageOptions && (
                            <FormControl
                              sx={{ m: 1, width: "70%" }}
                              size="small"
                            >
                              <InputLabel id="demo-select-small">
                                Choose your language
                              </InputLabel>
                              <Select
                                value={values.languageCode}
                                name="languageCode"
                                label="Choose your language"
                                onChange={handleChange}
                              >
                                {option.languageOptions.map(
                                  (languageOption: LanguageOption) => {
                                    return (
                                      <MenuItem
                                        key={`option-${languageOption.code}`}
                                        value={languageOption.code}
                                      >
                                        {languageOption.name} (
                                        {languageOption.code})
                                      </MenuItem>
                                    );
                                  }
                                )}
                              </Select>
                            </FormControl>
                          )}
                        {values.countryId == option.value &&
                          option.languageOptions &&
                          touched.languageCode &&
                          errors.languageCode && (
                            <Typography
                              variant="body1"
                              color="error"
                              sx={{
                                ml: 1,
                              }}
                            >
                              {errors.languageCode}
                            </Typography>
                          )}
                      </Box>
                    </React.Fragment>
                  );
                })}
              </RadioGroup>
              {errors.countryId && (
                <Typography
                  variant="body1"
                  color="error"
                  sx={{
                    my: 2,
                  }}
                >
                  {errors.countryId}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <ScreenFlowFormButtons
                isSubmitting={isSubmitting}
                status={status}
                centered
                onBack={onBack}
              />
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};
